import {
  ArrowRight,
  AudioLines,
  Check,
  CircleHelp,
  Clock3,
  ExternalLink,
  Globe2,
  LifeBuoy,
  Link2,
  ListMusic,
  LockKeyhole,
  Mail,
  MonitorPlay,
  QrCode,
  Scale,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Users,
} from "lucide-react";
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { Language } from "./i18n";
import { publicMetadata, readSiteLanguage, usePublicDomTranslation } from "./public-i18n";
import type { PublicPage } from "./public-route";

interface PublicLanguageValue {
  language: Language;
  toggleLanguage: () => void;
}

const CHROME_WEB_STORE_URL =
  "https://chromewebstore.google.com/detail/keep-the-remote-player/bbbpfnapmlogkahfjjejngbgjilhkalc?utm_source=keeptheremote&utm_medium=support_page&utm_campaign=extension_install";

const PublicLanguageContext = createContext<PublicLanguageValue | null>(null);

function usePublicLanguage(): PublicLanguageValue {
  const value = useContext(PublicLanguageContext);
  if (value === null) throw new Error("Public language context is unavailable.");
  return value;
}

function usePageMetadata(page: PublicPage): void {
  const { language } = usePublicLanguage();
  useEffect(() => {
    const next = publicMetadata[language][page];
    document.title = next.title;
    const description = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    description?.setAttribute("content", next.description);
  }, [language, page]);
}

function Brand() {
  return (
    <a className="public-brand" href="/" aria-label="Keep the Remote home">
      <span className="public-brand__mark" aria-hidden="true">
        <AudioLines size={21} />
      </span>
      <span>Keep the Remote</span>
    </a>
  );
}

function PublicHeader({ page }: { page: PublicPage }) {
  const { language, toggleLanguage } = usePublicLanguage();
  return (
    <header className="public-header">
      <Brand />
      <nav aria-label="Main navigation">
        {page === "home" ? (
          <>
            <a href="#how-it-works">How it works</a>
            <a href="#features">Features</a>
          </>
        ) : (
          <a href="/">Home</a>
        )}
        <a aria-current={page === "privacy" ? "page" : undefined} href="/privacy">
          Privacy
        </a>
        <a aria-current={page === "terms" ? "page" : undefined} href="/terms">
          Terms
        </a>
        <a aria-current={page === "support" ? "page" : undefined} href="/support">
          Support
        </a>
      </nav>
      <button
        className="public-language-button"
        type="button"
        onClick={toggleLanguage}
        aria-label={language === "en" ? "เปลี่ยนเป็นภาษาไทย" : "Switch to English"}
      >
        <Globe2 size={15} />
        {language === "en" ? "TH" : "EN"}
      </button>
      <a className="public-header__cta" href="/host/">
        Host console
        <ArrowRight size={15} />
      </a>
    </header>
  );
}

function PublicFooter() {
  return (
    <footer className="public-footer">
      <div>
        <Brand />
        <p>Friends request. You keep control.</p>
      </div>
      <nav aria-label="Footer navigation">
        <a href="/privacy">Privacy</a>
        <a href="/terms">Terms</a>
        <a href="/support">Support</a>
        <a href="https://www.youtube.com/t/terms" rel="noreferrer" target="_blank">
          YouTube Terms
          <ExternalLink size={12} />
        </a>
      </nav>
      <p className="public-footer__note">Independent and not endorsed by YouTube or Google.</p>
    </footer>
  );
}

function PublicLayout({ page, children }: { page: PublicPage; children: ReactNode }) {
  usePageMetadata(page);
  return (
    <div className="public-shell">
      <PublicHeader page={page} />
      {children}
      <PublicFooter />
    </div>
  );
}

function PlayerPreview() {
  return (
    <div className="landing-preview" aria-label="Keep the Remote queue preview">
      <div className="landing-preview__glow" aria-hidden="true" />
      <div className="landing-preview__player">
        <div className="landing-preview__topline">
          <span>
            <span className="landing-preview__live" />
            Host player
          </span>
          <span>Connected</span>
        </div>
        <div className="landing-preview__now">
          <div className="landing-preview__art" aria-hidden="true">
            <AudioLines size={30} />
          </div>
          <div>
            <span>Now playing</span>
            <strong>Stay for the whole song</strong>
            <small>Host&apos;s YouTube session</small>
          </div>
        </div>
        <div className="landing-preview__timeline">
          <span />
        </div>
        <div className="landing-preview__time">
          <span>2:18</span>
          <span>3:47</span>
        </div>
      </div>
      <div className="landing-preview__queue">
        <div className="landing-preview__queue-title">
          <span>Up next</span>
          <span>3 requests</span>
        </div>
        {[
          ["01", "Midnight Drive", "requested by May"],
          ["02", "Neon Skyline", "requested by Alex"],
          ["03", "Sunday Energy", "requested by Pat"],
        ].map(([order, title, guest], index) => (
          <div className="landing-preview__request" key={order}>
            <span>{order}</span>
            <div className={`landing-preview__thumb landing-preview__thumb--${index + 1}`} />
            <p>
              <strong>{title}</strong>
              <small>{guest}</small>
            </p>
            {index === 0 ? <span className="landing-preview__next">Next</span> : null}
          </div>
        ))}
      </div>
      <div className="landing-preview__guest">
        <Smartphone size={17} />
        <span>
          <strong>Guest request added</strong>
          No remote handoff required
        </span>
        <Check size={16} />
      </div>
    </div>
  );
}

function LandingPage() {
  return (
    <PublicLayout page="home">
      <main className="landing-main">
        <section className="landing-hero">
          <div className="landing-hero__copy">
            <div className="landing-eyebrow">
              <Sparkles size={14} />
              The shared queue that respects the current song
            </div>
            <h1>
              Friends pick what&apos;s next.
              <em>You keep the remote.</em>
            </h1>
            <p>
              Share one QR code. Guests search or paste a YouTube link, while the current video
              keeps playing and every request waits its turn.
            </p>
            <div className="landing-hero__actions">
              <a className="public-button public-button--primary" href="/host/">
                Open host console
                <ArrowRight size={17} />
              </a>
              <a className="public-button public-button--quiet" href="#how-it-works">
                See how it works
              </a>
            </div>
            <div className="landing-hero__assurances" aria-label="Key benefits">
              <span>
                <Check size={14} /> No Wi-Fi password sharing
              </span>
              <span>
                <Check size={14} /> No guest account required
              </span>
              <span>
                <Check size={14} /> Host stays in control
              </span>
            </div>
          </div>
          <PlayerPreview />
        </section>

        <section className="landing-proof" aria-label="Product principles">
          <span>One screen</span>
          <i />
          <span>One orderly queue</span>
          <i />
          <span>Everyone gets a turn</span>
        </section>

        <section className="landing-problem">
          <div>
            <span className="landing-section-number">01 / Why</span>
            <h2>The couch should not become a control panel.</h2>
          </div>
          <div className="landing-problem__copy">
            <p>
              Passing around the YouTube app turns a relaxed night into skipped songs, abandoned
              queues, new ad contexts, and repeated explanations of which button not to press.
            </p>
            <p>
              Keep the Remote gives friends the part they actually need—requesting a song—without
              handing over playback.
            </p>
          </div>
        </section>

        <section className="landing-how" id="how-it-works">
          <div className="landing-section-heading">
            <span className="landing-section-number">02 / How it works</span>
            <h2>From “play this next” to playing next.</h2>
            <p>No app lesson. No remote handoff. No queue archaeology.</p>
          </div>
          <div className="landing-steps">
            <article>
              <span className="landing-step-icon">
                <MonitorPlay size={23} />
              </span>
              <small>Step 01</small>
              <h3>Keep YouTube on the host PC</h3>
              <p>
                Pair the browser extension with your room and leave a normal YouTube watch tab open.
              </p>
            </article>
            <article>
              <span className="landing-step-icon">
                <QrCode size={23} />
              </span>
              <small>Step 02</small>
              <h3>Put one QR code in the room</h3>
              <p>
                Guests scan the room link on any phone. They do not need your remote, Wi-Fi
                password, or YouTube account.
              </p>
            </article>
            <article>
              <span className="landing-step-icon">
                <ListMusic size={23} />
              </span>
              <small>Step 03</small>
              <h3>Let every request wait its turn</h3>
              <p>
                Search or paste a YouTube link. The extension advances the guest queue when the
                current video reaches its boundary.
              </p>
            </article>
          </div>
        </section>

        <section className="landing-features" id="features">
          <div className="landing-section-heading landing-section-heading--left">
            <span className="landing-section-number">03 / Built for the host</span>
            <h2>Control where it matters. Freedom where it helps.</h2>
          </div>
          <div className="landing-feature-grid">
            <article className="landing-feature landing-feature--wide">
              <div>
                <LockKeyhole size={22} />
                <h3>The current video is protected</h3>
                <p>
                  Guest requests enter the queue instead of replacing what is already playing. Skip
                  remains a host decision.
                </p>
              </div>
              <div className="landing-feature__boundary" aria-hidden="true">
                <span>Playing</span>
                <i />
                <span>Guest queue</span>
              </div>
            </article>
            <article className="landing-feature">
              <Globe2 size={22} />
              <h3>Your browser, your session</h3>
              <p>
                Playback remains in the host&apos;s normal YouTube tab and existing account context.
              </p>
            </article>
            <article className="landing-feature">
              <Users size={22} />
              <h3>Low-friction for guests</h3>
              <p>
                A name and a room link are enough. No guest installation or YouTube sign-in is
                required.
              </p>
            </article>
            <article className="landing-feature">
              <Clock3 size={22} />
              <h3>Queue rules you can trust</h3>
              <p>
                Set duration, cooldown, duplicate, live stream, Shorts, and queue-length limits.
              </p>
            </article>
            <article className="landing-feature">
              <ShieldCheck size={22} />
              <h3>Private by design</h3>
              <p>Pairing uses a short-lived code. Room QR generation stays local to the product.</p>
            </article>
          </div>
        </section>

        <section className="landing-faq">
          <div className="landing-section-heading landing-section-heading--left">
            <span className="landing-section-number">04 / Good to know</span>
            <h2>Straight answers before the first song.</h2>
          </div>
          <div className="landing-faq__grid">
            <article>
              <CircleHelp size={19} />
              <div>
                <h3>Do guests need the same Wi-Fi?</h3>
                <p>
                  No shared local network or Wi-Fi password is required. Guests only need internet
                  access to open the public room link.
                </p>
              </div>
            </article>
            <article>
              <CircleHelp size={19} />
              <div>
                <h3>Does it block YouTube ads?</h3>
                <p>
                  No. Keep the Remote does not block ads or bypass YouTube restrictions. Playback
                  stays in the host&apos;s existing browser session.
                </p>
              </div>
            </article>
            <article>
              <CircleHelp size={19} />
              <div>
                <h3>Do I need a smart TV app?</h3>
                <p>
                  No. The player runs in desktop Chrome, Edge, or Firefox. Connect or cast that PC
                  to the room&apos;s display as you normally would.
                </p>
              </div>
            </article>
            <article>
              <CircleHelp size={19} />
              <div>
                <h3>Where can I get help?</h3>
                <p>
                  Visit the support page for setup checks and troubleshooting, or email the support
                  team if the problem continues.
                </p>
              </div>
            </article>
          </div>
        </section>

        <section className="landing-final">
          <span className="landing-final__mark" aria-hidden="true">
            <AudioLines size={26} />
          </span>
          <div>
            <span>Pick a song. Keep the flow.</span>
            <h2>Your friends can have the queue. You keep the remote.</h2>
          </div>
          <a className="public-button public-button--primary" href="/host/">
            Start hosting
            <ArrowRight size={17} />
          </a>
        </section>
      </main>
    </PublicLayout>
  );
}

function PolicySection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h2>{title}</h2>
      {children}
    </section>
  );
}

function PrivacyPage() {
  return (
    <PublicLayout page="privacy">
      <main className="policy-main">
        <header className="policy-hero">
          <span className="policy-hero__icon">
            <ShieldCheck size={22} />
          </span>
          <p>Plain-language policy</p>
          <h1>Privacy at Keep the Remote</h1>
          <p>
            We use only the information needed to pair a player, run a room, synchronize its queue,
            and diagnose playback. We do not sell personal data or use it for advertising.
          </p>
          <span className="policy-effective">Effective July 29, 2026</span>
        </header>

        <div className="policy-layout">
          <aside aria-label="Privacy policy contents">
            <strong>On this page</strong>
            <a href="#information">Information handled</a>
            <a href="#use">How it is used</a>
            <a href="#cookies">Authentication cookies</a>
            <a href="#storage">Storage and retention</a>
            <a href="#sharing">Sharing</a>
            <a href="#controls">Your controls</a>
            <a href="#youtube">YouTube and Google</a>
          </aside>
          <article className="policy-content">
            <PolicySection title="The short version">
              <div className="policy-callout">
                <LockKeyhole size={20} />
                <p>
                  Keep the Remote does not read Google passwords, YouTube authentication cookies, or
                  browsing activity outside YouTube. Google sign-in shares the account&apos;s
                  verified email and stable account identifier with the server. It does not download
                  videos, block advertisements, or bypass YouTube restrictions.
                </p>
              </div>
            </PolicySection>

            <section id="information">
              <h2>Information handled by the service and extension</h2>
              <p>The service handles:</p>
              <ul>
                <li>
                  <strong>Private dashboard identity:</strong> a platform administrator or host
                  signing in with Google shares their verified email and stable Google account
                  identifier. The server uses these fields only to authorize platform
                  administrators, create or recover the user&apos;s host account, and create a
                  signed local authentication session. Any verified Google user may create a host
                  account; the administrator allowlist grants separate platform privileges.
                </li>
                <li>
                  <strong>YouTube watch-page information:</strong> the current URL and video ID,
                  title, playback position and duration, playback state, volume, Autoplay state,
                  advertisement-active status, and technical playback-boundary identifiers.
                </li>
                <li>
                  <strong>Room information:</strong> room and device identifiers, device name, queue
                  revision, requested video IDs and titles, guest display names, and queue
                  selection, start, or failure events.
                </li>
                <li>
                  <strong>Pairing and security information:</strong> a high-entropy credential
                  scoped to the paired server, connection state, recent command identifiers, and
                  ordinary network metadata such as IP address for security and abuse prevention.
                </li>
                <li>
                  <strong>Technical diagnostics:</strong> timestamps, extension version, connection
                  and synchronization events, Autoplay verification, errors, and health information.
                </li>
              </ul>
            </section>

            <section id="use">
              <h2>How information is used</h2>
              <p>Information is used only to:</p>
              <ul>
                <li>authenticate and authorize platform administrators and hosts;</li>
                <li>authenticate the paired playback device;</li>
                <li>synchronize the host-managed guest queue;</li>
                <li>wait for the current video before opening the next request;</li>
                <li>manage YouTube Autoplay at queue boundaries;</li>
                <li>show player health and diagnostics to the authorized host;</li>
                <li>
                  prevent duplicate consumption, diagnose failures, and secure the service; and
                </li>
                <li>provide support when a user specifically asks for investigation.</li>
              </ul>
            </section>

            <section id="cookies">
              <h2>Authentication cookies</h2>
              <p>
                Keep the Remote uses first-party cookies only for private-dashboard authentication
                and sign-in security. It does not use cookies for advertising, cross-site tracking,
                or audience analytics.
              </p>
              <ul>
                <li>
                  <strong>Host and platform session cookies:</strong> signed JWT cookies keep the
                  applicable private dashboard signed in for up to 30 days. They are renewed after
                  an authenticated request when fewer than 14 days remain. The cookies are HttpOnly,
                  SameSite=Lax, and Secure on HTTPS deployments.
                </li>
                <li>
                  <strong>Google OAuth state cookie:</strong> a temporary HttpOnly cookie keeps the
                  Google sign-in response tied to the browser that started it. It expires after 10
                  minutes and is deleted when the callback completes.
                </li>
              </ul>
              <p>
                These cookies are set only after a user chooses to sign in and are used only to
                provide the authentication and security that user requested. The sign-in page gives
                notice before the persistent cookie is set. Signing out deletes the applicable
                session cookie; browser controls can also remove or block it.
              </p>
            </section>

            <section id="storage">
              <h2>Local storage and server retention</h2>
              <p>
                The extension stores settings, its scoped device credential, queue mirror, pending
                acknowledgements, playback-tab claim, connection state, recent command identifiers,
                and up to 200 diagnostic entries in the browser extension&apos;s local storage.
                Unpairing removes the local credential and active queue state. Uninstalling removes
                that local storage.
              </p>
              <p>
                Production builds transmit pairing, queue, playback, and diagnostic information only
                to the fixed Keep the Remote origin shown in Options, using HTTPS and WSS. The
                server keeps the latest room/device state and operational audit records while the
                associated room or host exists. Suspending a host blocks access, closes its rooms,
                and preserves its account and room data for reactivation. The server stores each
                host&apos;s verified email, stable Google account identifier, display name, and
                suspension status. Deleting a room or host removes the corresponding live records. A
                deleted user may create a new host account by signing in again. Signed dashboard
                sessions are held in the authentication cookies described above rather than stored
                as active server-side session records. A self-hosted operator controls its own logs,
                backups, and retention.
              </p>
            </section>

            <section id="sharing">
              <h2>Sharing and service providers</h2>
              <p>
                Keep the Remote does not sell information. Information may be processed by the
                operator of the configured server and its infrastructure providers only as needed to
                operate, secure, and deliver the service, investigate abuse or security incidents,
                comply with law, or as otherwise permitted by the Chrome Web Store User Data Policy.
              </p>
              <p>
                YouTube use and private dashboard sign-in communicate with Google under
                Google&apos;s terms and privacy policy. Keep the Remote receives only the identity
                fields described above for login; it never receives the Google password or
                authentication cookies.
              </p>
            </section>

            <section id="controls">
              <h2>Your controls and deletion</h2>
              <p>
                You can unpair in extension Options, clear local diagnostics in Diagnostics, revoke
                the player from the host console, sign out to delete the dashboard authentication
                cookie, delete rooms, or uninstall the extension. For host-account deletion or other
                server-side access or deletion requests concerning the bundled Keep the Remote
                service, email{" "}
                <a href="mailto:privacy@keeptheremote.com">privacy@keeptheremote.com</a>. If you use
                a self-hosted server, contact its operator.
              </p>
              <p>
                For technical support, email{" "}
                <a href="mailto:support@keeptheremote.com">support@keeptheremote.com</a>. Never send
                a local session, room bearer URL, pairing code, device credential, OAuth client
                secret, API key, or other secret.
              </p>
            </section>

            <PolicySection title="Limited Use commitment">
              <p>
                Keep the Remote Player&apos;s use and transfer of information received from Google
                APIs adheres to the{" "}
                <a
                  href="https://developer.chrome.com/docs/webstore/program-policies/limited-use"
                  rel="noreferrer"
                  target="_blank"
                >
                  Chrome Web Store User Data Policy
                </a>
                , including Limited Use requirements. Information is used only to provide or improve
                the disclosed single purpose and related security, reliability, and support
                operations.
              </p>
            </PolicySection>

            <section id="youtube">
              <h2>YouTube and Google</h2>
              <p>
                Keep the Remote is independent and is not endorsed by YouTube or Google. Use of
                YouTube is subject to the{" "}
                <a href="https://www.youtube.com/t/terms" rel="noreferrer" target="_blank">
                  YouTube Terms of Service
                </a>{" "}
                and{" "}
                <a href="https://policies.google.com/privacy" rel="noreferrer" target="_blank">
                  Google Privacy Policy
                </a>
                .
              </p>
            </section>

            <PolicySection title="Changes to this policy">
              <p>
                This policy may be updated when data practices or legal requirements change. If an
                extension update changes how user data is handled, the change will be prominently
                disclosed before the new collection or use begins. The effective date above
                identifies the current version.
              </p>
            </PolicySection>
          </article>
        </div>
      </main>
    </PublicLayout>
  );
}

function TermsPage() {
  return (
    <PublicLayout page="terms">
      <main className="policy-main">
        <header className="policy-hero">
          <span className="policy-hero__icon">
            <Scale size={22} />
          </span>
          <p>Plain-language terms</p>
          <h1>Terms for using Keep the Remote</h1>
          <p>
            These terms explain the rules for using the Keep the Remote website, host console, guest
            rooms, service, and browser extension.
          </p>
          <span className="policy-effective">Terms effective July 29, 2026</span>
        </header>

        <div className="policy-layout">
          <aside aria-label="Terms of Service contents">
            <strong>On this page</strong>
            <a href="#agreement">Agreement and eligibility</a>
            <a href="#responsibilities">Your responsibilities</a>
            <a href="#third-parties">YouTube and media rights</a>
            <a href="#accounts">Accounts and room security</a>
            <a href="#availability">Availability and termination</a>
            <a href="#disclaimers">Disclaimers and liability</a>
          </aside>
          <article className="policy-content">
            <PolicySection title="The short version">
              <div className="policy-callout">
                <Scale size={20} />
                <p>
                  Keep the Remote manages requests and a queue. It does not provide YouTube content,
                  music licences, public-performance rights, or permission to use any third-party
                  service. Hosts remain responsible for what they play and where they play it.
                </p>
              </div>
            </PolicySection>

            <section id="agreement">
              <h2>Agreement and eligibility</h2>
              <p>
                These Terms of Service (“Terms”) apply when you access or use Keep the Remote,
                including its public website, host console, guest rooms, server features, and Keep
                the Remote Player browser extension (together, the “Service”). By using the Service,
                you agree to these Terms and the <a href="/privacy">Privacy Policy</a>. If you do
                not agree, do not use the Service.
              </p>
              <p>
                You must be legally able to agree to these Terms. If you are a minor where you live,
                you may use the Service only with permission from a parent or legal guardian. If you
                use the Service for an organization, you confirm that you can accept these Terms for
                that organization.
              </p>
              <p>
                A self-hosted deployment is provided by its operator. That operator is responsible
                for the deployment and may apply additional terms, policies, or restrictions.
              </p>
            </section>

            <section id="service">
              <h2>What the Service does</h2>
              <p>
                Keep the Remote lets a host create a room, pair a supported browser with that room,
                and let guests submit video requests to a host-managed queue. The host controls
                playback and queue settings. Guests receive no right to control the host&apos;s
                device or third-party account beyond the features the host makes available.
              </p>
              <p>
                Any user with a verified Google identity may create a host account. Platform
                administration is a separate privilege and is not granted merely by creating a host
                account.
              </p>
              <p>
                Features may depend on a supported browser, the extension, network connectivity,
                YouTube, Google sign-in, or other third-party services. Those services can change,
                restrict, or stop features without Keep the Remote&apos;s control.
              </p>
            </section>

            <section id="responsibilities">
              <h2>Your responsibilities and acceptable use</h2>
              <p>You agree to:</p>
              <ul>
                <li>follow applicable laws and the terms and policies of third-party services;</li>
                <li>respect copyright, privacy, publicity, and other rights;</li>
                <li>
                  use rooms, guest names, requests, and messages only for their intended purpose;
                </li>
                <li>keep credentials, pairing codes, and private room links secure; and</li>
                <li>
                  supervise your room and guests when you host, including removing inappropriate
                  requests.
                </li>
              </ul>
              <p>You must not:</p>
              <ul>
                <li>use the Service for unlawful, harmful, fraudulent, or abusive activity;</li>
                <li>
                  probe, disrupt, overload, bypass, or gain unauthorized access to the Service,
                  another room, account, device, or network;
                </li>
                <li>
                  distribute malware, scrape the Service, automate abusive requests, or interfere
                  with security or rate limits;
                </li>
                <li>impersonate another person or submit deceptive or infringing material; or</li>
                <li>
                  use Keep the Remote to bypass advertisements, access restrictions, or safeguards
                  imposed by YouTube or another provider.
                </li>
              </ul>
            </section>

            <section id="third-parties">
              <h2>YouTube, Google, and media rights</h2>
              <p>
                Keep the Remote is independent and is not endorsed by YouTube or Google. Your use of
                YouTube and Google remains subject to the{" "}
                <a href="https://www.youtube.com/t/terms" rel="noreferrer" target="_blank">
                  YouTube Terms of Service
                </a>
                , the{" "}
                <a href="https://policies.google.com/terms" rel="noreferrer" target="_blank">
                  Google Terms of Service
                </a>
                , and their other applicable policies.
              </p>
              <p>
                Keep the Remote does not sell or license videos, music, or other third-party
                content. You are responsible for ensuring that your playback is permitted, including
                any copyright, subscription, venue, public-performance, or commercial-use rights
                required where you use the Service.
              </p>
              <p>
                Third-party content and services may be unavailable, inaccurate, interrupted,
                removed, or restricted by account, age, region, advertisement, consent, or other
                rules. Keep the Remote does not control and is not responsible for them.
              </p>
            </section>

            <section id="requests">
              <h2>Room requests and information you provide</h2>
              <p>
                You keep any rights you have in names, requests, and other information you submit.
                You give the operator a limited, non-exclusive, worldwide, royalty-free licence to
                host, process, reproduce, and transmit that information only as needed to operate,
                secure, maintain, and support the Service.
              </p>
              <p>
                Do not submit confidential information or content you are not allowed to share. A
                host may view, reject, remove, or otherwise moderate requests in their room.
              </p>
            </section>

            <section id="accounts">
              <h2>Accounts, pairing, and room security</h2>
              <p>
                You are responsible for activity through your host access, paired players, and
                private room links. Keep credentials and one-time pairing codes confidential, use
                accurate account information, and promptly revoke access you no longer trust. Tell{" "}
                <a href="mailto:support@keeptheremote.com">support@keeptheremote.com</a> if you
                believe the bundled Service has been accessed without authorization.
              </p>
              <p>
                Your verified Google identity identifies your host account across sessions. Deleting
                the account removes its live rooms and related data, but does not prevent that
                identity from registering again later.
              </p>
              <p>
                We may limit, suspend, or revoke access to protect users or the Service, investigate
                suspected abuse, comply with law, or enforce these Terms. Suspending a host blocks
                host access and closes its rooms while preserving the account for possible
                reactivation or deletion.
              </p>
            </section>

            <section id="availability">
              <h2>Service availability, changes, and termination</h2>
              <p>
                The Service may change, gain or lose features, experience interruptions, or be
                discontinued. We do not promise that a particular browser, extension version,
                third-party integration, video, or feature will remain available.
              </p>
              <p>
                You may stop using the Service at any time. Hosts can revoke a player or delete any
                room under their control, including their final room, and users can uninstall the
                extension. Contact the operator to request deletion of a bundled Service host
                account. We may suspend or terminate access for a material or repeated breach of
                these Terms, security risk, legal requirement, or conduct that may harm the Service,
                its users, or others.
              </p>
            </section>

            <section id="ownership">
              <h2>Ownership</h2>
              <p>
                Keep the Remote and its branding, interface, and software are protected by
                intellectual-property laws. These Terms do not transfer ownership of the Service or
                third-party content to you. Any open-source components remain governed by their
                applicable licences.
              </p>
            </section>

            <section id="disclaimers">
              <h2>Disclaimers and limitation of liability</h2>
              <p>
                To the extent permitted by law, the Service is provided “as is” and “as available.”
                We make no warranty that it will be uninterrupted, secure, error-free, or suitable
                for a particular event, device, venue, or purpose. Mandatory consumer rights are not
                excluded.
              </p>
              <p>
                To the extent permitted by law, the operator and its contributors will not be liable
                for indirect, incidental, special, consequential, exemplary, or punitive damages, or
                for loss of profits, revenue, goodwill, data, media access, or business opportunity,
                arising from the Service or third-party content and services.
              </p>
              <p>
                To the extent permitted by law, total liability for claims relating to the Service
                is limited to the greater of the amount you paid for the Service during the 12
                months before the event giving rise to the claim or USD 100. This limit does not
                apply where liability cannot legally be limited.
              </p>
            </section>

            <PolicySection title="General terms">
              <p>
                If part of these Terms is unenforceable, the remaining terms stay in effect. A
                failure to enforce a term is not a waiver. Nothing in these Terms limits rights or
                obligations that cannot be limited under applicable law.
              </p>
            </PolicySection>

            <PolicySection title="Changes to these Terms">
              <p>
                We may update these Terms to reflect changes to the Service, law, security, or abuse
                risks. The effective date identifies the current version. If a change is material,
                we will provide reasonable notice where practical. Using the Service after the new
                Terms take effect means you accept them.
              </p>
            </PolicySection>

            <PolicySection title="Contact">
              <p>
                Questions about these Terms can be sent to{" "}
                <a href="mailto:support@keeptheremote.com">support@keeptheremote.com</a>. For a
                self-hosted deployment, contact that deployment&apos;s operator.
              </p>
            </PolicySection>
          </article>
        </div>
      </main>
    </PublicLayout>
  );
}

function SupportPage() {
  return (
    <PublicLayout page="support">
      <main className="support-main">
        <section className="support-hero">
          <span className="support-hero__icon">
            <LifeBuoy size={24} />
          </span>
          <div>
            <p>Keep the Remote support</p>
            <h1>Get the music moving again.</h1>
            <p>
              Start with the quick checks below. If the problem remains, email
              support@keeptheremote.com with the extension version and a non-secret diagnostic
              message.
            </p>
          </div>
          <div className="support-hero__actions">
            <a
              className="public-button public-button--primary"
              href={CHROME_WEB_STORE_URL}
              target="_blank"
              rel="noreferrer"
            >
              Install extension
              <ExternalLink size={16} />
            </a>
            <a
              className="public-button public-button--quiet"
              href="mailto:support@keeptheremote.com"
            >
              Email support
              <Mail size={16} />
            </a>
          </div>
        </section>

        <section className="support-setup">
          <div className="landing-section-heading landing-section-heading--left">
            <span className="landing-section-number">Quick setup</span>
            <h2>Four steps to a healthy player.</h2>
          </div>
          <ol>
            <li>
              <span>1</span>
              <div>
                <h3>Install the extension</h3>
                <p>Use the button above, then open Options on the playback PC.</p>
              </div>
            </li>
            <li>
              <span>2</span>
              <div>
                <h3>Create a room pairing code</h3>
                <p>Open the host console and generate a fresh one-time code.</p>
              </div>
            </li>
            <li>
              <span>3</span>
              <div>
                <h3>Pair the browser extension</h3>
                <p>Review the disclosure in Options and enter the code.</p>
              </div>
            </li>
            <li>
              <span>4</span>
              <div>
                <h3>Open YouTube and confirm readiness</h3>
                <p>Start a normal watch video, then check that Diagnostics is healthy.</p>
              </div>
            </li>
          </ol>
        </section>

        <section className="support-grid">
          <article>
            <span>
              <Link2 size={20} />
            </span>
            <h2>Pairing failed or authorization was revoked</h2>
            <p>
              Revoke the old player in the host console, create a new pairing code, and pair again.
              Codes are short-lived and can be used only once.
            </p>
          </article>
          <article>
            <span>
              <MonitorPlay size={20} />
            </span>
            <h2>No active YouTube watch tab</h2>
            <p>
              Open a normal <code>youtube.com/watch</code> page and start playback. Shorts, search
              results, and the YouTube homepage are not playback tabs.
            </p>
          </article>
          <article>
            <span>
              <ListMusic size={20} />
            </span>
            <h2>The queue is not synchronized</h2>
            <p>
              Use Reconnect &amp; resync in Diagnostics. Confirm the bundled server is reachable and
              the player has not been claimed by another browser.
            </p>
          </article>
          <article>
            <span>
              <Globe2 size={20} />
            </span>
            <h2>YouTube shows a prompt or restriction</h2>
            <p>
              Resolve account, age, region, advertisement, consent, or inactivity prompts normally.
              Keep the Remote does not bypass YouTube restrictions.
            </p>
          </article>
        </section>

        <section className="support-diagnostics">
          <div>
            <span className="landing-section-number">Before contacting support</span>
            <h2>Share context, never credentials.</h2>
            <p>
              Include the extension version, approximate time, browser version, what you expected,
              and the non-secret diagnostic message.
            </p>
          </div>
          <div>
            <strong>Never share:</strong>
            <ul>
              <li>dashboard cookie or recovery token</li>
              <li>room bearer URL</li>
              <li>pairing code or player credential</li>
              <li>YouTube API key or OAuth token</li>
              <li>Google account credentials</li>
            </ul>
          </div>
        </section>

        <section className="support-links">
          <a href="/privacy">
            <ShieldCheck size={19} />
            <span>
              <strong>Read the privacy policy</strong>
              Learn what the extension handles and why.
            </span>
            <ArrowRight size={17} />
          </a>
          <a href="mailto:support@keeptheremote.com">
            <Mail size={19} />
            <span>
              <strong>Email support</strong>
              Get help with setup or troubleshooting.
            </span>
            <ArrowRight size={17} />
          </a>
        </section>
      </main>
    </PublicLayout>
  );
}

export function PublicPages({ page }: { page: PublicPage }) {
  const [language, setLanguage] = useState<Language>(readSiteLanguage);
  usePublicDomTranslation(language);
  const languageValue = useMemo(
    () => ({
      language,
      toggleLanguage: () => setLanguage((current) => (current === "en" ? "th" : "en")),
    }),
    [language],
  );
  const content =
    page === "home" ? (
      <LandingPage />
    ) : page === "privacy" ? (
      <PrivacyPage />
    ) : page === "terms" ? (
      <TermsPage />
    ) : (
      <SupportPage />
    );
  return (
    <PublicLanguageContext.Provider value={languageValue}>{content}</PublicLanguageContext.Provider>
  );
}
